import React, { useState, useEffect, useRef, useCallback } from "react";
import Plyr from "plyr";
import "plyr/dist/plyr.css";
import "../../../dist/assets/stylesheets/css/videoPlayback.css";
import Box from "@mui/material/Box";
import { Icon } from "@iconify/react";
import { trialScrubMediaTime, trialScrubStep } from "../../util/trialPlaybackKeys";

const LAYOUT_MODES = {
  COMPACT: "compact",
  WIDE: "wide",
  FULLSCREEN: "fullscreen",
};

const VIDEO_MIME_TYPES = {
  webm: "video/webm",
  ogv: "video/ogg",
  ogg: "video/ogg",
};

// Browsers skip a <source> whose declared type they cannot decode, so the hint
// has to follow the actual file. Trial videos are mp4 unless stated otherwise.
export function resolveVideoMimeType(src) {
  if (typeof src !== "string") return "video/mp4";
  const ext = src.split("?")[0].split("#")[0].split(".").pop();
  return VIDEO_MIME_TYPES[String(ext).toLowerCase()] || "video/mp4";
}

function TrialVideoPlayback(props) {
  const {
    video,
    videoSrc,
    videoRef,
    duration,
    onSeek,
    onTogglePlay,
    onLoadedMetadata,
    onLayoutModeChange,
    onTimeSync,
  } = props;

  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isWideView, setIsWideView] = useState(false);
  const containerRef = useRef(null);
  const plyrRef = useRef(null);
  const timelineRef = useRef(null);
  const isScrubbingRef = useRef(false);
  const legacyVideo = !!video && !videoSrc;

  // Callers pass inline handlers, so these are read through refs to keep the
  // player setup effect tied to the source only. Recreating Plyr on every
  // render destroys the media element mid-playback.
  const onLoadedMetadataRef = useRef(onLoadedMetadata);
  const onTimeSyncRef = useRef(onTimeSync);
  onLoadedMetadataRef.current = onLoadedMetadata;
  onTimeSyncRef.current = onTimeSync;

  const layoutMode = isFullscreen
    ? LAYOUT_MODES.FULLSCREEN
    : isWideView
      ? LAYOUT_MODES.WIDE
      : LAYOUT_MODES.COMPACT;

  useEffect(() => {
    onLayoutModeChange?.(layoutMode);
  }, [layoutMode, onLayoutModeChange]);

  useEffect(() => {
    if (legacyVideo || !videoSrc || !videoRef) return undefined;

    const videoEl = videoRef.current;
    if (!videoEl) return undefined;

    const player = new Plyr(videoEl, {
      controls: [],
      clickToPlay: false,
      hideControls: true,
      resetOnEnd: false,
      keyboard: { focused: true, global: false },
      tooltips: { controls: false, seek: true },
      fullscreen: { enabled: true, container: "#trial-video-playback" },
    });

    plyrRef.current = player;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => {
      setIsPlaying(false);
      setIsBuffering(false);
    };
    // A trial video that runs dry mid-playback stalls without firing `pause`,
    // so surface it instead of leaving the user with a frozen picture.
    const handleWaiting = () => setIsBuffering(true);
    const handleResumed = () => setIsBuffering(false);
    const handleDurationUpdate = (event) => onLoadedMetadataRef.current?.(event);
    const handleTimeSync = () => {
      const sync = onTimeSyncRef.current;
      if (typeof sync === "function" && !Number.isNaN(videoEl.currentTime)) {
        sync(videoEl.currentTime);
      }
    };

    videoEl.addEventListener("play", handlePlay);
    videoEl.addEventListener("pause", handlePause);
    videoEl.addEventListener("waiting", handleWaiting);
    videoEl.addEventListener("stalled", handleWaiting);
    videoEl.addEventListener("playing", handleResumed);
    videoEl.addEventListener("canplay", handleResumed);
    videoEl.addEventListener("loadedmetadata", handleDurationUpdate);
    videoEl.addEventListener("durationchange", handleDurationUpdate);
    videoEl.addEventListener("progress", handleDurationUpdate);
    videoEl.addEventListener("canplay", handleDurationUpdate);
    player.on("timeupdate", handleTimeSync);
    player.on("seeked", handleTimeSync);

    setIsPlaying(!videoEl.paused);

    return () => {
      videoEl.removeEventListener("play", handlePlay);
      videoEl.removeEventListener("pause", handlePause);
      videoEl.removeEventListener("waiting", handleWaiting);
      videoEl.removeEventListener("stalled", handleWaiting);
      videoEl.removeEventListener("playing", handleResumed);
      videoEl.removeEventListener("canplay", handleResumed);
      videoEl.removeEventListener("loadedmetadata", handleDurationUpdate);
      videoEl.removeEventListener("durationchange", handleDurationUpdate);
      videoEl.removeEventListener("progress", handleDurationUpdate);
      videoEl.removeEventListener("canplay", handleDurationUpdate);
      player.off("timeupdate", handleTimeSync);
      player.off("seeked", handleTimeSync);
      player.destroy();
      plyrRef.current = null;
    };
  }, [legacyVideo, videoSrc, videoRef]);

  useEffect(() => {
    if (legacyVideo || !videoRef?.current) return;
    const vid = videoRef.current;
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    vid.addEventListener("play", handlePlay);
    vid.addEventListener("pause", handlePause);
    setIsPlaying(!vid.paused);

    return () => {
      vid.removeEventListener("play", handlePlay);
      vid.removeEventListener("pause", handlePause);
    };
  }, [legacyVideo, videoRef, video]);

  // The scrub position is written straight to the input rather than held in
  // state: re-rendering these controls every frame re-serialises all of their
  // MUI styles, which is enough main-thread work to stutter video playback.
  useEffect(() => {
    if (legacyVideo || !videoRef) return undefined;

    let rafId = null;
    const tick = () => {
      const el = videoRef.current;
      const input = timelineRef.current;
      if (el && input && !isScrubbingRef.current && !Number.isNaN(el.currentTime)) {
        const next = String(el.currentTime);
        if (input.value !== next) {
          input.value = next;
        }
      }
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [legacyVideo, videoRef]);

  // Capture phase so this runs before Plyr, which would otherwise jump 10s
  // while its container is focused. Each arrow moves one frame.
  useEffect(() => {
    const onKey = (event) => {
      const el = videoRef?.current;
      if (!el || typeof onSeek !== "function" || !(duration > 0)) return;
      const step = trialScrubStep(event);
      if (step == null) return;
      event.preventDefault();
      event.stopPropagation();
      const fps = 30;
      onSeek(trialScrubMediaTime(el.currentTime, Math.round(duration * fps), fps, step));
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [videoRef, duration, onSeek]);

  useEffect(() => {
    const handleFsChange = () => {
      const el =
        document.fullscreenElement || document.webkitFullscreenElement;
      setIsFullscreen(!!el);
    };
    document.addEventListener("fullscreenchange", handleFsChange);
    document.addEventListener("webkitfullscreenchange", handleFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFsChange);
      document.removeEventListener("webkitfullscreenchange", handleFsChange);
    };
  }, []);

  const handleToggleFullscreen = useCallback(() => {
    const player = plyrRef.current;
    if (player?.fullscreen) {
      player.fullscreen.toggle();
      return;
    }

    const container = containerRef.current;
    if (!container) return;
    if (!document.fullscreenElement && container.requestFullscreen) {
      container.requestFullscreen();
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
    }
  }, []);

  // Toggling immediately rather than after a double-click grace period: a
  // deferred toggle fires a quarter second after the pointer has moved on, which
  // reads as the video stopping by itself. The two clicks of a double-click
  // cancel each other out, so playback still survives a fullscreen toggle.
  const handleVideoAreaClick = () => {
    onTogglePlay?.();
  };

  const handleVideoAreaDoubleClick = () => {
    handleToggleFullscreen();
  };

  const showCustomControls =
    !!videoRef &&
    typeof duration === "number" &&
    duration > 0 &&
    typeof onSeek === "function" &&
    typeof onTogglePlay === "function";

  const videoContent = legacyVideo ? (
    video
  ) : (
    <video
      ref={videoRef}
      className="view plyr-react"
      key={videoSrc}
      playsInline
      preload="auto"
      onLoadedMetadata={onLoadedMetadata}
      onError={props.onError}
    >
      {videoSrc ? (
        <source src={videoSrc} type={resolveVideoMimeType(videoSrc)} />
      ) : null}
    </video>
  );

  return (
    <Box
      ref={containerRef}
      id="trial-video-playback"
      className={`trial-video-playback trial-video-playback--${layoutMode}`}
      display="flex"
      flexDirection="column"
      width="100%"
      sx={{ backgroundColor: "#fff" }}
    >
      <Box
        className={`trial-video-container trial-video-container--${layoutMode}${
          isWideView ? " trial-video-container--wide" : ""
        }`}
        width="100%"
        flex={1}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
          backgroundColor: "#fff",
          cursor: onTogglePlay ? "pointer" : undefined,
        }}
        onClick={handleVideoAreaClick}
        onDoubleClick={handleVideoAreaDoubleClick}
        position="relative"
      >
        {videoContent}
        {isBuffering && (
          <span className="trial-video-buffering" role="status">
            Buffering…
          </span>
        )}
      </Box>
      {showCustomControls && (
        <Box
          className={
            isFullscreen
              ? "trial-controls trial-controls--fullscreen"
              : "trial-controls"
          }
          px={isFullscreen ? 4 : 3}
          py={isFullscreen ? 2 : 0.5}
          display="flex"
          alignItems="center"
          gap={isFullscreen ? 3 : 1.5}
          sx={{
            background: isFullscreen ? "rgba(0,0,0,0.82)" : "#fff",
            color: isFullscreen ? "#fff" : "rgba(0,0,0,0.87)",
            borderTop: isFullscreen
              ? "1px solid rgba(255,255,255,0.12)"
              : "1px solid rgba(0,0,0,0.1)",
          }}
        >
          <button
            type="button"
            className="trial-control-btn"
            aria-label={isPlaying ? "Pause" : "Play"}
            onClick={onTogglePlay}
            style={{
              borderRadius: "50%",
              width: isFullscreen ? 44 : 32,
              height: isFullscreen ? 44 : 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: isFullscreen
                ? "rgba(255,255,255,0.18)"
                : "rgba(0,0,0,0.06)",
              border: "none",
              color: "inherit",
            }}
          >
            <Icon
              icon={
                isPlaying
                  ? "material-symbols:pause"
                  : "material-symbols:play-arrow"
              }
              fontSize={isFullscreen ? 28 : 22}
            />
          </button>
          <input
            className={
              "trial-timeline" +
              (isFullscreen ? " trial-timeline--fullscreen" : "")
            }
            type="range"
            ref={timelineRef}
            min={0}
            max={duration || 0}
            step={0.01}
            defaultValue={0}
            onPointerDown={() => {
              isScrubbingRef.current = true;
            }}
            onPointerUp={() => {
              isScrubbingRef.current = false;
            }}
            onPointerCancel={() => {
              isScrubbingRef.current = false;
            }}
            onChange={(e) => onSeek(Number(e.target.value))}
            style={{
              flexGrow: 1,
              height: isFullscreen ? 8 : 6,
              borderRadius: 999,
              background: isFullscreen
                ? "rgba(255,255,255,0.2)"
                : "rgba(255,255,255,0.7)",
              accentColor: isFullscreen ? "#fff" : "#9e9e9e",
            }}
          />
          <Box display="flex" alignItems="center" gap={1}>
            <button
              type="button"
              className="trial-control-btn"
              aria-label={isWideView ? "Compact view" : "Wide view"}
              onClick={() => !isFullscreen && setIsWideView((prev) => !prev)}
              disabled={isFullscreen}
              style={{
                borderRadius: "50%",
                width: isFullscreen ? 40 : 28,
                height: isFullscreen ? 40 : 28,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: isWideView
                  ? isFullscreen
                    ? "rgba(255,255,255,0.2)"
                    : "rgba(0,0,0,0.08)"
                  : isFullscreen
                    ? "rgba(255,255,255,0.12)"
                    : "transparent",
                border: "none",
                color: "inherit",
                opacity: isFullscreen ? 0.5 : 1,
                cursor: isFullscreen ? "not-allowed" : "pointer",
              }}
            >
              <Icon
                icon="material-symbols:aspect-ratio"
                fontSize={isFullscreen ? 24 : 18}
              />
            </button>
            <button
              type="button"
              className="trial-control-btn"
              aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
              onClick={handleToggleFullscreen}
              style={{
                borderRadius: "50%",
                width: isFullscreen ? 40 : 28,
                height: isFullscreen ? 40 : 28,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: isFullscreen
                  ? "rgba(255,255,255,0.12)"
                  : "transparent",
                border: "none",
                color: "inherit",
              }}
            >
              <Icon
                icon={
                  isFullscreen
                    ? "material-symbols:fullscreen-exit"
                    : "material-symbols:fullscreen"
                }
                fontSize={isFullscreen ? 24 : 18}
              />
            </button>
          </Box>
        </Box>
      )}
    </Box>
  );
}

// Memoised so playback does not re-render the controls; the scrub position is
// updated imperatively above.
export default React.memo(TrialVideoPlayback);
